"use client";

import { createAnalyticsClient } from "@repo/analytics";
import { isProductionTelemetryEnabled } from "../telemetry";
import {
  identifyAmplitudeUser,
  resetAmplitudeUser,
  setAmplitudeUserProperties,
  trackAmplitudeEvent,
} from "./amplitude";
import { getAttributionProperties } from "./attribution";
import { GTMEvents, trackPageView as pushGTMPageView } from "./gtm";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * App event name constants (sent to GTM dataLayer → GA4).
 * Mapped events use GA4 recommended names where applicable.
 */
export const AnalyticsEvents = {
  // Authentication
  AUTH_SIGN_UP_STARTED: "auth_sign_up_started",
  AUTH_SIGN_UP_COMPLETED: "auth_sign_up_completed",
  AUTH_SIGN_UP_FAILED: "auth_sign_up_failed",
  AUTH_SIGN_IN_STARTED: "auth_sign_in_started",
  AUTH_SIGN_IN_COMPLETED: "auth_sign_in_completed",
  AUTH_SIGN_IN_FAILED: "auth_sign_in_failed",
  AUTH_GOOGLE_SIGN_IN_CLICKED: "auth_google_sign_in_clicked",
  AUTH_PASSWORD_RESET_REQUESTED: "auth_password_reset_requested",
  AUTH_PASSWORD_RESET_COMPLETED: "auth_password_reset_completed",
  AUTH_SIGN_OUT_CLICKED: "auth_sign_out_clicked",
  AUTH_DIALOG_OPENED: "auth_dialog_opened",

  // Product Interactions
  PRODUCT_VIEWED: "product_viewed",
  PRODUCT_CARD_CLICKED: "product_card_clicked",
  PRODUCT_IMAGE_HOVERED: "product_image_hovered",
  PRODUCT_VARIANT_SELECTED: "product_variant_selected",
  PRODUCT_QUANTITY_CHANGED: "product_quantity_changed",
  PRODUCT_ADDED_TO_CART: "product_added_to_cart",
  PRODUCT_ADDED_TO_WISHLIST: "product_added_to_wishlist",
  PRODUCT_REMOVED_FROM_WISHLIST: "product_removed_from_wishlist",
  PRODUCT_IMAGE_ZOOMED: "product_image_zoomed",
  PRODUCT_LIGHTBOX_OPENED: "product_lightbox_opened",
  PRODUCT_REVIEW_LINK_CLICKED: "product_review_link_clicked",

  // Cart Operations
  CART_OPENED: "cart_opened",
  CART_CLOSED: "cart_closed",
  CART_CHECKOUT_CLICKED: "cart_checkout_clicked",
  CART_QUANTITY_UPDATED: "cart_quantity_updated",
  CART_ITEM_REMOVED: "cart_item_removed",
  CART_CONTINUE_SHOPPING: "cart_continue_shopping",

  // Product Comparison
  PRODUCT_COMPARE_ADDED: "product_compare_added",
  PRODUCT_COMPARE_REMOVED: "product_compare_removed",
  PRODUCT_COMPARE_LIMIT_REACHED: "product_compare_limit_reached",
  COMPARE_PAGE_VIEWED: "compare_page_viewed",
  COMPARE_CLEARED: "compare_cleared",
  COMPARE_VARIANT_CHANGED: "compare_variant_changed",
  COMPARE_ADD_TO_CART: "compare_add_to_cart",

  // Checkout
  CHECKOUT_STARTED: "checkout_started",
  CHECKOUT_SHIPPING_INFO_ENTERED: "checkout_shipping_info_entered",
  CHECKOUT_PAYMENT_ELEMENT_READY: "checkout_payment_element_ready",
  CHECKOUT_PAYMENT_STARTED: "checkout_payment_started",
  CHECKOUT_PAYMENT_SUCCEEDED: "checkout_payment_succeeded",
  CHECKOUT_PAYMENT_FAILED: "checkout_payment_failed",
  CHECKOUT_ORDER_CREATED: "checkout_order_created",
  CHECKOUT_ABANDONED: "checkout_abandoned",
  CHECKOUT_ABANDONMENT_FEEDBACK_SUBMITTED: "checkout_abandonment_feedback_submitted",
  CHECKOUT_ABANDONMENT_FEEDBACK_DISMISSED: "checkout_abandonment_feedback_dismissed",
  CHECKOUT_NOTES_ADDED: "checkout_notes_added",

  // Search and Discovery
  SEARCH_OPENED: "search_opened",
  SEARCH_PERFORMED: "search_performed",
  SEARCH_RESULT_CLICKED: "search_result_clicked",
  SEARCH_NO_RESULTS: "search_no_results",
  SEARCH_CLOSED: "search_closed",
  CATEGORY_FILTER_APPLIED: "category_filter_applied",
  PRODUCT_SORT_CHANGED: "product_sort_changed",
  PAGINATION_CHANGED: "pagination_changed",
  BREADCRUMB_CLICKED: "breadcrumb_clicked",

  // User Account
  ACCOUNT_PROFILE_VIEWED: "account_profile_viewed",
  ACCOUNT_PROFILE_UPDATED: "account_profile_updated",
  ACCOUNT_ADDRESS_UPDATED: "account_address_updated",
  ACCOUNT_WISHLIST_VIEWED: "account_wishlist_viewed",
  ACCOUNT_ORDERS_VIEWED: "account_orders_viewed",
  ACCOUNT_MENU_OPENED: "account_menu_opened",
  ACCOUNT_SETTINGS_CLICKED: "account_settings_clicked",

  // Reviews and Ratings
  REVIEW_WRITE_CLICKED: "review_write_clicked",
  REVIEW_FORM_OPENED: "review_form_opened",
  REVIEW_SUBMITTED: "review_submitted",
  REVIEW_FAILED: "review_failed",
  REVIEW_STAR_SELECTED: "review_star_selected",
  REVIEW_PAGE_CHANGED: "review_page_changed",

  // Affiliate Program
  AFFILIATE_PAGE_VIEWED: "affiliate_page_viewed",
  AFFILIATE_SIGN_UP_CLICKED: "affiliate_sign_up_clicked",
  AFFILIATE_ID_CREATED: "affiliate_id_created",
  AFFILIATE_ID_CREATION_FAILED: "affiliate_id_creation_failed",
  AFFILIATE_LINK_COPIED: "affiliate_link_copied",
  AFFILIATE_PAYOUT_REQUESTED: "affiliate_payout_requested",
  AFFILIATE_PAYOUT_METHOD_UPDATED: "affiliate_payout_method_updated",

  // Newsletter and Contact
  NEWSLETTER_SUBSCRIBED: "newsletter_subscribed",
  NEWSLETTER_SUBSCRIPTION_FAILED: "newsletter_subscription_failed",
  CONTACT_FORM_SUBMITTED: "contact_form_submitted",
  CONTACT_FORM_FAILED: "contact_form_failed",
  SOCIAL_LINK_CLICKED: "social_link_clicked",
  CUSTOMER_SERVICE_LINK_CLICKED: "customer_service_link_clicked",

  SUPPORT_CHAT_OPENED: "support_chat_opened",
  SUPPORT_CHAT_MESSAGE_SENT: "support_chat_message_sent",
  SUPPORT_CHAT_EMAIL_ESCALATION: "support_chat_email_escalation",
  SUPPORT_CHAT_ERROR: "support_chat_error",

  // Blog and Content
  BLOG_LIST_VIEWED: "blog_list_viewed",
  BLOG_POST_CLICKED: "blog_post_clicked",
  BLOG_POST_VIEWED: "blog_post_viewed",
  BLOG_PAGE_CHANGED: "blog_page_changed",
  BLOG_BACK_NAV_CLICKED: "blog_back_nav_clicked",
  FAQ_VIEWED: "faq_viewed",

  // UI Interactions
  THEME_CHANGED: "theme_changed",
  COUNTRY_CHANGED: "country_changed",
  LANGUAGE_CHANGED: "language_changed",
  MOBILE_NAV_OPENED: "mobile_nav_opened",
  GREETING_MODAL_CLOSED: "greeting_modal_closed",
  GREETING_REGISTER_CLICKED: "greeting_register_clicked",
} as const;

type EventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

interface BaseEventProperties {
  locale?: string;
  country?: string;
  currency?: string;
  referrer?: string;
  timestamp?: number;
}

const analyticsClient = createAnalyticsClient({
  app: "tutuly",
  env: process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || "development",
});

function pushToGtag(eventName: string, properties: Record<string, unknown>): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", eventName, properties);
}

/** Sends events to the GTM dataLayer for GA4 (and Google Ads where configured). */
export function trackEvent(
  eventName: EventName,
  properties: Record<string, unknown> & BaseEventProperties = {},
) {
  if (!isProductionTelemetryEnabled()) {
    return;
  }

  try {
    const enrichedProperties = {
      ...getAttributionProperties(),
      ...properties,
    };

    trackAmplitudeEvent(eventName, enrichedProperties);
    pushToGTMDataLayer(eventName, {
      ...enrichedProperties,
      timestamp: Date.now(),
    });
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("Analytics tracking error:", error);
    }
  }
}

function pushToGTMDataLayer(
  eventName: EventName,
  properties: Record<string, unknown> & BaseEventProperties,
): void {
  try {
    const eventMapping: Record<string, string> = {
      [AnalyticsEvents.PRODUCT_VIEWED]: GTMEvents.VIEW_ITEM,
      [AnalyticsEvents.PRODUCT_ADDED_TO_CART]: GTMEvents.ADD_TO_CART,
      [AnalyticsEvents.CHECKOUT_STARTED]: GTMEvents.BEGIN_CHECKOUT,
      [AnalyticsEvents.CHECKOUT_PAYMENT_SUCCEEDED]: GTMEvents.PURCHASE,
      [AnalyticsEvents.AUTH_SIGN_UP_COMPLETED]: GTMEvents.SIGN_UP,
      [AnalyticsEvents.AUTH_SIGN_IN_COMPLETED]: GTMEvents.LOGIN,
      [AnalyticsEvents.SEARCH_PERFORMED]: GTMEvents.SEARCH,
      [AnalyticsEvents.NEWSLETTER_SUBSCRIBED]: GTMEvents.GENERATE_LEAD,
      [AnalyticsEvents.CONTACT_FORM_SUBMITTED]: GTMEvents.CONTACT,
    };

    const gtmEventName = eventMapping[eventName];
    if (!gtmEventName) {
      analyticsClient.track(eventName, {
        event_name: eventName,
        ...properties,
      });
      pushToGtag(eventName, properties);
      return;
    }

    const ecommerceData: Record<string, unknown> = {};

    if (Array.isArray(properties.items)) {
      ecommerceData.items = properties.items;
    } else if (properties.product_id || properties.item_id) {
      ecommerceData.items = [
        {
          item_id: properties.product_id || properties.item_id,
          item_name: properties.product_name || properties.item_name || properties.name,
          item_brand: properties.brand || "TUTULY",
          item_category: properties.category,
          item_variant: properties.variant,
          price: properties.price,
          quantity: properties.quantity || 1,
          currency: properties.currency,
        },
      ];
    }

    if (properties.currency) {
      ecommerceData.currency = properties.currency;
    }

    if (properties.value !== undefined || properties.price !== undefined) {
      ecommerceData.value = properties.value ?? properties.price;
    }

    if (properties.order_id || properties.transaction_id) {
      ecommerceData.transaction_id = properties.order_id || properties.transaction_id;
    }

    if (properties.tax !== undefined) {
      ecommerceData.tax = properties.tax;
    }

    if (properties.shipping !== undefined) {
      ecommerceData.shipping = properties.shipping;
    }

    if (properties.coupon !== undefined) {
      ecommerceData.coupon = properties.coupon;
    }

    analyticsClient.track(gtmEventName, {
      ...(Object.keys(ecommerceData).length > 0 ? { ecommerce: ecommerceData } : {}),
      /** Legacy key name; GTM triggers may reference it — rename in GTM before changing. */
      amplitude_event: eventName,
      ...properties,
    });
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("GTM dataLayer push error:", error);
    }
  }
}

function pushUserDataLayer(payload: Record<string, unknown>): void {
  try {
    const eventName =
      typeof payload.event === "string" && payload.event.length > 0
        ? payload.event
        : "custom_event";

    analyticsClient.track(eventName, payload);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("GTM user data push error:", error);
    }
  }
}

/** Associate a signed-in user with GA4 (via dataLayer / gtag when available). */
export function identifyUser(userId: string, traits: Record<string, unknown> = {}) {
  if (!isProductionTelemetryEnabled() || typeof window === "undefined") return;
  try {
    const enrichedTraits = {
      ...getAttributionProperties(),
      ...traits,
    };

    identifyAmplitudeUser(userId, enrichedTraits);
    pushUserDataLayer({
      event: "user_identified",
      user_id: userId,
      ...enrichedTraits,
    });
    if (typeof window.gtag === "function") {
      window.gtag("set", { user_id: userId });
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("GA4 identify error:", error);
    }
  }
}

/** Set GA4 user_properties-style fields (dataLayer + gtag when present). */
export function setUserProperties(properties: Record<string, unknown>) {
  if (!isProductionTelemetryEnabled() || typeof window === "undefined") return;
  try {
    const filtered: Record<string, string | number | boolean> = {};
    const enrichedProperties = {
      ...getAttributionProperties(),
      ...properties,
    };

    for (const [key, value] of Object.entries(enrichedProperties)) {
      if (value !== undefined && value !== null) {
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
          filtered[key] = value;
        }
      }
    }
    setAmplitudeUserProperties(filtered);
    pushUserDataLayer({
      event: "user_properties_updated",
      user_properties: filtered,
    });
    if (typeof window.gtag === "function" && Object.keys(filtered).length > 0) {
      window.gtag("set", "user_properties", filtered);
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("GA4 set user properties error:", error);
    }
  }
}

/** Clear user association on sign-out. */
export function resetUser() {
  if (!isProductionTelemetryEnabled() || typeof window === "undefined") return;
  try {
    resetAmplitudeUser();
    pushUserDataLayer({
      event: "user_reset",
      user_id: undefined,
    });
    if (typeof window.gtag === "function") {
      window.gtag("set", { user_id: "" });
    }
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("GA4 reset error:", error);
    }
  }
}

/** Page view via GTM dataLayer (GA4 compatible). */
export function trackPageView(
  path: string,
  title: string,
  additionalProperties: Record<string, unknown> = {},
) {
  if (!isProductionTelemetryEnabled()) {
    return;
  }

  const enrichedProperties = {
    ...getAttributionProperties(),
    ...additionalProperties,
  };
  const pageViewProperties = {
    page_path: path,
    page_title: title,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    ...enrichedProperties,
  };
  pushGTMPageView(path, title, enrichedProperties);
  pushToGtag(GTMEvents.PAGE_VIEW, pageViewProperties);
}

export function getDeviceType(): string {
  if (typeof window === "undefined") return "unknown";

  const width = window.innerWidth;
  if (width < 640) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

export function getEmailDomain(email: string): string | undefined {
  if (!email?.includes("@")) return undefined;
  return email.split("@")[1];
}

export function useTracking() {
  return {
    trackEvent,
    identifyUser,
    setUserProperties,
    resetUser,
    trackPageView,
    getDeviceType,
    AnalyticsEvents,
  };
}
